import { Injectable } from '@angular/core';
import { Store } from '../../../services/store';
import { map, Observable, of, tap } from 'rxjs';
import { ShipmentProvider } from './shipment.provider';
import { ShipmentHeader, ShipmentStatus } from './models/shipment.dto';
import { SHIPMENT_STATUS_LABELS, ShipmentGroupVm, ShipmentItemVm, ShipmentVm } from './models/shipment.view-model';
import { toVoid } from '../../../utils/void';
import { parseAmount } from '../../../utils/format-amount';

export interface ShipmentState {
  loading: boolean;
  /**
   * Undefined until the header is created — lines are added from the client-order page
   * to a saved shipment.
   */
  shipment: ShipmentVm | undefined;
}

const INITIAL_STATE: ShipmentState = {
  loading: false,
  shipment: undefined
};

@Injectable({ providedIn: 'root' })
export class ShipmentStore extends Store<ShipmentState> {
  constructor(private provider: ShipmentProvider) {
    super(INITIAL_STATE);
  }

  init(id: number | 'new'): Observable<void> {
    if (id === 'new') {
      return of(undefined);
    }

    this.update(state => ({ ...state, loading: true }));

    return this.provider.getShipment(id)
      .pipe(
        map(ShipmentVm.mapModelToViewModel),
        tap(shipment => {
          this.update(state => ({ ...state, shipment, loading: false }));
        }),
        toVoid()
      );
  }

  createShipment(header: ShipmentHeader): Observable<void> {
    return this.provider.createShipment(header)
      .pipe(
        tap(header => {
          // a fresh shipment has no lines yet — the header response is the whole shipment
          this.update(state => ({ ...state, shipment: ShipmentVm.mapModelToViewModel({ ...header, groups: [] }) }));
        }),
        toVoid()
      );
  }

  /**
   * The whole header travels each time — the view shows the new values right away and
   * the backend gets the same shape CreateShipment took.
   */
  patchHeader(header: ShipmentHeader): Observable<void> {
    const shipment = this.state.shipment;

    if (!shipment) {
      throw new Error('Cannot edit the header of a shipment that is not saved yet.');
    }

    this.update(state => ({ ...state, shipment: { ...state.shipment!, ...header } }));

    return this.provider.patchShipment(shipment.id, header);
  }

  /**
   * The partial-shipment gesture: how many pieces of the position this wysyłka carries.
   * The backend keeps it within the position's unshipped remainder.
   */
  updateItemAmount(shipmentItemId: number, value: string): Observable<void> {
    const amount = parseAmount(value);
    const shipment = this.state.shipment;

    if (!shipment) {
      throw new Error('Cannot change a line of a shipment that is not saved yet.');
    }

    if (!(amount > 0)) {
      return of(undefined);
    }

    this.updateGroups(groups => groups.map(group => ({
      ...group,
      items: group.items.map(item => item.shipmentItemId === shipmentItemId ? { ...item, amount } : item)
    })));

    return this.provider.patchShipmentItemAmount(shipment.id, shipmentItemId, amount);
  }

  /**
   * Takes the position off the shipment; a zamówienie with no lines left leaves the view.
   */
  deleteItem(shipmentItemId: number): Observable<void> {
    const shipment = this.state.shipment;

    if (!shipment) {
      throw new Error('Cannot remove a line from a shipment that is not saved yet.');
    }

    this.updateGroups(groups => groups
      .map(group => ({ ...group, items: group.items.filter(item => item.shipmentItemId !== shipmentItemId) }))
      .filter(group => group.items.length > 0));

    return this.provider.deleteShipmentItem(shipment.id, shipmentItemId);
  }

  ship(): Observable<void> {
    const shipment = this.state.shipment;

    if (!shipment) {
      throw new Error('Cannot ship a shipment that is not saved yet.');
    }

    // the backend stamps the exact time; the page only needs the day
    this.setStatus(ShipmentStatus.shipped, new Date().toISOString());

    return this.provider.shipShipment(shipment.id);
  }

  unship(): Observable<void> {
    const shipment = this.state.shipment;

    if (!shipment) {
      throw new Error('Cannot un-ship a shipment that is not saved yet.');
    }

    this.setStatus(ShipmentStatus.planned, null);

    return this.provider.unshipShipment(shipment.id);
  }

  private setStatus(status: ShipmentStatus, shippedAt: string | null): void {
    this.update(state => ({
      ...state,
      shipment: {
        ...state.shipment!,
        status,
        statusLabel: SHIPMENT_STATUS_LABELS[status],
        shippedAt
      }
    }));
  }

  private updateGroups(updateGroups: (groups: ShipmentGroupVm[]) => ShipmentGroupVm[]): void {
    this.update(state => ({
      ...state,
      shipment: {
        ...state.shipment!,
        groups: updateGroups(state.shipment!.groups)
      }
    }));
  }
}
